import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";
import BackgroundVideo from "@/components/BackgroundVideo";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useFusionAccess } from "@/lib/useFusionAccess";

const STREAK_BADGES = { 3: "🔥 Starter", 7: "⚡ One Week", 14: "💪 Two Weeks", 30: "🏆 Iron Month", 60: "🚀 Machine", 100: "👑 Century" };

function StreakPill() {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/logs/streak").then((r) => setS(r.data)).catch(() => {});
  }, []);
  if (!s || s.streak === 0) return null;
  const topBadge = s.earned_badges?.length ? STREAK_BADGES[s.earned_badges[s.earned_badges.length - 1]] : null;
  return (
    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-orange-500/15 border border-orange-400/30 text-xs font-black text-orange-300"
      title={s.next_badge ? `${s.days_to_next} day(s) to the ${STREAK_BADGES[s.next_badge]} badge` : "Max badge earned!"}
      data-testid="log-streak-pill">
      <Flame className="h-3.5 w-3.5" /> {s.streak}-day streak
      {topBadge && <span className="font-bold text-orange-200/90">· {topBadge}</span>}
      {!s.logged_today && <span className="text-orange-200/70 font-semibold">· log today to keep it</span>}
    </div>
  );
}
import { toast } from "sonner";
import { LogOut, Check, AlertTriangle, History, HeartPulse, Dumbbell, BookOpen, Snowflake, ListChecks, Sparkles, Camera, Gauge, Flame, Activity, Video, Zap, TrendingUp } from "lucide-react";
import PlayerAvatarUploader from "@/components/PlayerAvatarUploader";
import PlayerNotificationBell from "@/components/PlayerNotificationBell";
import PlayerWorkloadCard from "@/components/PlayerWorkloadCard";
import PlayerVerdictHero from "@/components/PlayerVerdictHero";
import PushSettingsCard from "@/components/PushSettingsCard";
import HybridToleranceSlider from "@/components/HybridToleranceSlider";
import ColemanScenarioPicker from "@/components/ScenarioPicker";
import BodyScanMap from "@/components/BodyScanMap";
import TargetedRemedyCard from "@/components/TargetedRemedyCard";
import CustomRoutinePanel from "@/components/CustomRoutinePanel";
import DailyThrowsBreakdown from "@/components/DailyThrowsBreakdown";

const HYBRIDS = ["Hybrid A", "Hybrid B", "Hybrid C", "Hybrid D", "Hybrid E"];
const ENVS = ["Flat Ground", "Long Toss", "Bullpen", "Live BP", "Game Outing", "Rest"];

const RATING_LABELS = {
  arm_health: ["Severe Pain", "Stiff", "OK", "Strong", "100% Fresh"],
  body_soreness: ["Wrecked", "Sore", "Average", "Fresh", "Fully Recovered"],
  sleep_quality: ["Restless <6h", "Poor", "OK", "Good", "Restorative >8h"],
  mental_energy: ["Drained", "Low", "Focused", "Hungry", "Peak Drive"],
  post_throw_fatigue: ["No Fatigue", "Mild", "Moderate", "Heavy", "Exhausted"],
};

function Rating({ label, sub, value, onChange, testId }) {
  const labels = RATING_LABELS[testId] || [];
  return (
    <div className="rounded-xl ink-card p-4 dark-slider" data-testid={`rating-${testId}`}>
      <div className="flex items-baseline justify-between">
        <div>
          <div className="font-display font-bold text-base text-white">{label}</div>
          {sub && <div className="text-xs text-white/50 mt-0.5">{sub}</div>}
        </div>
        <div className="font-display font-black text-4xl font-mono-num text-white">{value}</div>
      </div>
      <Slider value={[value]} min={1} max={5} step={1} onValueChange={(v) => onChange(v[0])} className="mt-4" data-testid={`slider-${testId}`} />
      <div className="mt-2 text-xs text-white/60 text-center">{labels[value - 1]}</div>
    </div>
  );
}

export default function PlayerPortal() {
  const { user, logout, team, refresh } = useAuth();
  const nav = useNavigate();
  const access = useFusionAccess();
  // Fusion-only athletes (joined via the coach's Fusion invite link, team has no
  // BioHacker plan) live in the Fusion dashboard, not the full BioHacker portal.
  useEffect(() => {
    if (access.loaded && !access.biohacker_enabled) {
      nav("/portal/fusion", { replace: true });
    }
  }, [access.loaded, access.biohacker_enabled]); // eslint-disable-line
  const [profileData, setProfileData] = useState(null);
  const [form, setForm] = useState({
    arm_health: 4, body_soreness: 4, sleep_quality: 4, mental_energy: 4,
    scheduled_hybrid: "Hybrid B", throwing_environment: "Flat Ground",
    max_distance: 90, throw_volume: 50,
    throw_breakdown: { catch_play: 0, long_toss: 0, pre_throw_routine: 0, bullpen: 0, live_game: 0, plyo: 0 },
    chest_accuracy_75: true, pain_during: false, post_throw_fatigue: 2,
    body_scan: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [scenario, setScenario] = useState(null);  // Coleman demo scenario overlay
  const [showScenario, setShowScenario] = useState(false);

  const toggles = team?.daily_log_toggles || {};

  // When the Coleman demo scenario is active, overlay its readiness/hybrid/score
  // onto the live profileData so the rest of the page reflects the demo state.
  const effectiveProfile = scenario && profileData ? {
    ...profileData,
    readiness: {
      ...profileData.readiness,
      score: scenario.score,
      band: scenario.score >= 80 ? "peak" : scenario.score >= 65 ? "good" : scenario.score >= 50 ? "caution" : "risk",
      suggested_hybrid: scenario.hybrid,
    },
    next_track: {
      ...profileData.next_track,
      hybrid: `Hybrid ${scenario.hybrid}`,
      note: scenario.note,
    },
  } : profileData;

  const load = async () => {
    const { data } = await api.get("/players/me/profile");
    setProfileData(data);
    // Auto-default today's hybrid to the engine's suggestion, but only if the
    // player hasn't already chosen one (lets them override at any time).
    const sug = data?.readiness?.suggested_hybrid;
    if (sug) {
      setForm((prev) => prev._user_picked_hybrid ? prev : { ...prev, scheduled_hybrid: `Hybrid ${sug}` });
    }
  };
  useEffect(() => { load(); }, []);

  const setVal = (k, v) => setForm((prev) => ({ ...prev, [k]: v, ...(k === "scheduled_hybrid" ? { _user_picked_hybrid: true } : {}) }));

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/logs", form);
      setSubmitted(data);
      toast.success("Daily log submitted");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  const handleLogout = async () => { await logout(); nav("/", { replace: true }); };
  const showField = (k) => toggles[k] !== false;
  const inputCls = "bg-[var(--ink-1)] border-[var(--ink-border)] text-white font-mono-num";

  if (!profileData) return <div className="min-h-screen flex items-center justify-center text-sm text-white/50" style={{ backgroundColor: "var(--ink-1)" }}>Loading…</div>;

  return (
    <div className="min-h-screen pb-32" style={{ backgroundColor: "var(--ink-1)" }}>
      <header className="sticky top-0 z-30 backdrop-blur-xl border-b border-[var(--ink-border)]" style={{ backgroundColor: "rgba(10,20,36,0.85)" }}>
        <div className="max-w-2xl mx-auto px-5 py-4 flex items-center justify-between gap-3">
          <Logo size="sm" variant="lockup" linkTo="/portal" dark />
          <div className="flex items-center gap-1 overflow-x-auto whitespace-nowrap min-w-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="portal-header-nav">
            <Link to="/portal/apple-health" data-testid="player-apple-health-link">
              <Button variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/5">
                <HeartPulse className="h-4 w-4 mr-1.5" /> Biometrics
              </Button>
            </Link>
            {access.fusion_enabled && (
              <Link to="/portal/fusion" data-testid="player-fusion-link">
                <Button variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/5">
                  <Flame className="h-4 w-4 mr-1.5" /> Fusion
                </Button>
              </Link>
            )}
            <Link to="/portal/livecoach" data-testid="player-livecoach-link">
              <Button variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/5">
                <Video className="h-4 w-4 mr-1.5" /> Live Coach
              </Button>
            </Link>
            <a href="/player-guide" target="_blank" rel="noreferrer" data-testid="player-guide-link">
              <Button variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/5">
                <BookOpen className="h-4 w-4 mr-1.5" /> Guide
              </Button>
            </a>
            <StreakPill />
            <Link to="/portal/history" data-testid="player-history-link">
              <Button variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/5">
                <History className="h-4 w-4 mr-1.5" /> History
              </Button>
            </Link>
            <PlayerNotificationBell />
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="player-logout-button" className="text-white/70 hover:text-white hover:bg-white/5">
              <LogOut className="h-4 w-4 mr-1.5" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-5 pt-6">
        <div className="flex items-center justify-end mb-2 relative z-10">
          <button
            onClick={() => setShowScenario((v) => !v)}
            className="text-[10px] uppercase tracking-[0.18em] font-semibold inline-flex items-center gap-1.5 hover:underline"
            style={{ color: "var(--brand-secondary)" }}
            data-testid="scenario-toggle"
          >
            <Sparkles className="h-3 w-3" />
            {showScenario ? "Hide demo scenario" : "Try a sample game day"}
          </button>
        </div>
        {showScenario && (
          <div className="mt-3">
            <ColemanScenarioPicker onScenarioChange={setScenario} />
          </div>
        )}
        {/* Next track card */}
        <div className="rounded-2xl p-6 text-white relative overflow-hidden border border-[var(--ink-border-strong)]" style={{ backgroundColor: "var(--ink-2)" }}>
          <div className="absolute inset-0 opacity-30 pointer-events-none">
            <BackgroundVideo tintClass="bg-gradient-to-br from-[rgba(15,30,54,0.85)] to-[rgba(5,11,22,0.95)]" />
          </div>
          <div className="relative">
            <div className="text-[10px] tracking-[0.3em] font-bold uppercase" style={{ color: "var(--brand-secondary)" }}>
              Your Next Track
            </div>
            <div className="mt-2 font-display font-black text-4xl md:text-5xl tracking-tighter">
              {submitted?.next_track?.hybrid || effectiveProfile.next_track?.hybrid}
            </div>
            <div className="text-white/70 text-sm mt-1 uppercase tracking-[0.18em] font-semibold">
              {submitted?.next_track?.intensity || effectiveProfile.next_track?.intensity}
            </div>
            <p className="text-white/80 text-sm mt-4 leading-relaxed">
              {submitted?.next_track?.note || effectiveProfile.next_track?.note}
            </p>
            {(submitted?.log?.status || profileData.latest_log?.status) && (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <StatusBadge status={submitted?.log?.status || profileData.latest_log?.status} />
                {(submitted?.log?.auto_hold_reason || profileData.latest_log?.auto_hold_reason) && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-[0.1em] uppercase"
                    style={{ backgroundColor: "rgba(245,158,11,0.15)", color: "#FCD34D", border: "1px solid rgba(245,158,11,0.35)" }}
                    data-testid="auto-hold-chip">
                    HRV Auto-Hold · {(submitted?.log?.auto_hold_reason || profileData.latest_log?.auto_hold_reason)}
                  </span>
                )}
              </div>
            )}
            {effectiveProfile?.readiness?.suggested_hybrid && (() => {
              const sug = effectiveProfile.readiness.suggested_hybrid;
              const c = { A: "#D90429", B: "#F59E0B", C: "#3B82F6", D: "#22D3EE", E: "#16A34A" }[sug] || "#22D3EE";
              return (
                <div className="mt-4 flex items-center gap-3 rounded-lg p-3" style={{ backgroundColor: `${c}1f`, border: `1px solid ${c}55` }} data-testid="suggested-hybrid-chip">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-full font-display font-black text-lg" style={{ backgroundColor: c, color: "#fff" }}>{sug}</span>
                  <div className="flex-1">
                    <div className="text-[10px] tracking-[0.25em] font-bold uppercase" style={{ color: c }}>Today's Suggested Hybrid</div>
                    <div className="text-sm text-white/80">Readiness <span className="font-mono-num font-bold text-white">{effectiveProfile.readiness.score}</span> · band <span className="font-bold capitalize text-white">{effectiveProfile.readiness.band}</span></div>
                  </div>
                  <Link to="/portal/routine" className="text-xs underline" style={{ color: c }}>View routine →</Link>
                </div>
              );
            })()}
          </div>
        </div>

        <div className="mt-6">
          <PlayerVerdictHero />
        </div>

        <div className="mt-6">
          <div className="flex items-center gap-4 mb-3">
            <PlayerAvatarUploader avatarUrl={user?.avatar_url} name={user?.name} onChange={() => refresh?.()} size={84} />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] tracking-[0.3em] font-bold text-white/40 uppercase">Daily Log · {new Date().toLocaleDateString()}</div>
              <h1 className="font-display font-black text-3xl tracking-tighter mt-0.5 text-white truncate">
                Hey, {user?.name?.split(" ")[0]}.
              </h1>
              <p className="text-sm text-white/50 mt-1">Drop today's readiness check-in below. Takes under 30 seconds.</p>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Link to="/portal/plan" data-testid="link-plan">
              <Button size="sm" className="text-white font-bold red-glow" style={{ backgroundColor: "var(--brand-secondary)" }}>
                <ListChecks className="h-4 w-4 mr-2" /> Today's Plan
              </Button>
            </Link>
            <Link to="/portal/routine" data-testid="link-routine">
              <Button variant="outline" size="sm" className="border-white/15 text-white bg-white/5 hover:bg-white/10">
                <Dumbbell className="h-4 w-4 mr-2" /> Warm-Up
              </Button>
            </Link>
            <Link to="/portal/recovery" data-testid="link-recovery">
              <Button variant="outline" size="sm" className="border-white/15 text-white bg-white/5 hover:bg-white/10">
                <Snowflake className="h-4 w-4 mr-2" /> Recovery
              </Button>
            </Link>
            <Link to="/onboarding/screening" data-testid="link-screening">
              <Button variant="outline" size="sm" className="border-cyan-400/40 text-cyan-200 bg-cyan-500/5 hover:bg-cyan-500/10">
                <Activity className="h-4 w-4 mr-2" /> MotionIQ Screen
              </Button>
            </Link>
            <Link to="/pitch-mechanics" data-testid="link-pitch-mechanics">
              <Button variant="outline" size="sm" className="border-[var(--brand-secondary)]/40 text-white bg-[var(--brand-secondary)]/10 hover:bg-[var(--brand-secondary)]/20">
                <Video className="h-4 w-4 mr-2" /> Pitch Mechanics 2D/3D
              </Button>
            </Link>
            <Link to="/speediq" data-testid="link-speediq">
              <Button variant="outline" size="sm" className="border-amber-400/40 text-amber-100 bg-amber-500/5 hover:bg-amber-500/10">
                <Zap className="h-4 w-4 mr-2" /> SpeedIQ
              </Button>
            </Link>
            <Link to="/portal/speediq/history" data-testid="link-speediq-history">
              <Button variant="outline" size="sm" className="border-amber-400/30 text-amber-100/85 bg-amber-500/5 hover:bg-amber-500/10">
                <TrendingUp className="h-4 w-4 mr-2" /> SpeedIQ timeline
              </Button>
            </Link>
            <Link to="/portal/history" data-testid="link-history">
              <Button variant="outline" size="sm" className="border-white/15 text-white bg-white/5 hover:bg-white/10">
                <History className="h-4 w-4 mr-2" /> History
              </Button>
            </Link>
            <Link to="/portal/apple-health" data-testid="link-health">
              <Button variant="outline" size="sm" className="border-white/15 text-white bg-white/5 hover:bg-white/10">
                <HeartPulse className="h-4 w-4 mr-2" /> Apple Health
              </Button>
            </Link>
            <Link to="/portal/livup" data-testid="link-livup">
              <Button variant="outline" size="sm" className="border-white/15 text-white bg-white/5 hover:bg-white/10">
                <HeartPulse className="h-4 w-4 mr-2" /> LIVUP ring
              </Button>
            </Link>
            <Link to="/portal/telemetry" data-testid="link-telemetry">
              <Button variant="outline" size="sm" className="border-white/15 text-white bg-white/5 hover:bg-white/10">
                <Gauge className="h-4 w-4 mr-2" /> Telemetry
              </Button>
            </Link>
            <Link to="/portal/warmup" data-testid="link-warmup">
              <Button variant="outline" size="sm" className="border-white/15 text-white bg-white/5 hover:bg-white/10">
                <Flame className="h-4 w-4 mr-2" /> Warmup check-in
              </Button>
            </Link>
            <Link to="/portal/lessons" data-testid="link-bookings">
              <Button variant="outline" size="sm" className="border-white/15 text-white bg-white/5 hover:bg-white/10">
                <BookOpen className="h-4 w-4 mr-2" /> My Lessons
              </Button>
            </Link>
            <Link to="/portal/help" data-testid="link-help">
              <Button variant="outline" size="sm" className="border-white/15 text-white bg-white/5 hover:bg-white/10">
                <BookOpen className="h-4 w-4 mr-2" /> How To
              </Button>
            </Link>
            {user?.role === "solo_player" && (
              <Link to="/portal/billing" data-testid="link-billing">
                <Button variant="outline" size="sm" className="border-white/15 text-white bg-white/5 hover:bg-white/10">
                  <ListChecks className="h-4 w-4 mr-2" /> My Billing
                </Button>
              </Link>
            )}
          </div>
        </div>

        <div className="mt-6 space-y-4">
          <BodyScanMap value={form.body_scan} onChange={(entries) => setVal("body_scan", entries)} />

          {/* Custom routine override from coaching staff */}
          {effectiveProfile?.routine_override && (
            <CustomRoutinePanel override={effectiveProfile.routine_override} />
          )}

          {/* Targeted Remedy Card — appears after submit OR if /profile has remedies (today) */}
          {(submitted?.remedies?.length > 0 || effectiveProfile?.remedies?.length > 0) && (
            <TargetedRemedyCard
              remedies={submitted?.remedies || effectiveProfile?.remedies || []}
              criticalMode={Boolean(
                submitted?.sharp_pain_zones?.length ||
                (effectiveProfile?.latest_log?.sharp_pain_zones || []).length
              )}
              sharpPainZones={
                submitted?.sharp_pain_zones?.length
                  ? submitted.sharp_pain_zones
                  : (effectiveProfile?.latest_log?.sharp_pain_zones || [])
              }
            />
          )}

          {showField("arm_health") && <Rating label="Arm Health" sub="1 = severe pain · 5 = 100% fresh" value={form.arm_health} onChange={(v) => setVal("arm_health", v)} testId="arm_health" />}
          {showField("body_soreness") && <Rating label="Body Soreness" sub="1 = wrecked · 5 = fully recovered" value={form.body_soreness} onChange={(v) => setVal("body_soreness", v)} testId="body_soreness" />}
          {showField("sleep_quality") && <Rating label="Sleep Quality" sub="1 = restless · 5 = restorative >8h" value={form.sleep_quality} onChange={(v) => setVal("sleep_quality", v)} testId="sleep_quality" />}
          {showField("mental_energy") && <Rating label="Mental Energy" sub="1 = drained · 5 = peak competitive drive" value={form.mental_energy} onChange={(v) => setVal("mental_energy", v)} testId="mental_energy" />}

          {(showField("scheduled_hybrid") || showField("throwing_environment")) && (
            <div className="rounded-xl ink-card p-4 space-y-4">
              {showField("scheduled_hybrid") && (
                <div>
                  <label className="font-display font-bold text-sm text-white">Today's hybrid track</label>
                  {effectiveProfile?.readiness?.suggested_hybrid && (
                    <div className="mt-2">
                      <HybridToleranceSlider
                        suggestedLetter={effectiveProfile.readiness.suggested_hybrid}
                        currentLetter={(form.scheduled_hybrid || "").replace("Hybrid ", "")}
                        onChange={(letter) => setVal("scheduled_hybrid", `Hybrid ${letter}`)}
                      />
                    </div>
                  )}
                  <details className="mt-2">
                    <summary className="text-[11px] text-white/40 cursor-pointer hover:text-white/70 select-none">
                      Or pick any hybrid manually ▾
                    </summary>
                    <Select value={form.scheduled_hybrid} onValueChange={(v) => setVal("scheduled_hybrid", v)}>
                      <SelectTrigger className={`mt-2 h-11 ${inputCls}`} data-testid="hybrid-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{HYBRIDS.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                    </Select>
                  </details>
                </div>
              )}
              {showField("throwing_environment") && (
                <div>
                  <label className="font-display font-bold text-sm text-white">Throwing environment</label>
                  <Select value={form.throwing_environment} onValueChange={(v) => setVal("throwing_environment", v)}>
                    <SelectTrigger className={`mt-2 h-11 ${inputCls}`} data-testid="env-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{ENVS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}

          {(showField("max_distance") || showField("throw_volume")) && (
            <div className="space-y-4">
              {showField("max_distance") && (
                <div className="rounded-xl ink-card p-4">
                  <label className="font-display font-bold text-sm text-white">Max distance (ft)</label>
                  <Input type="number" inputMode="numeric" min={0} value={form.max_distance === 0 ? "" : form.max_distance} placeholder="0"
                    onChange={(e) => setVal("max_distance", Math.max(0, Number(e.target.value) || 0))}
                    className={`mt-2 h-12 text-xl ${inputCls}`} data-testid="max-distance-input" />
                </div>
              )}
              {showField("throw_volume") && (
                <DailyThrowsBreakdown
                  value={form.throw_breakdown}
                  inputCls={inputCls}
                  onChange={(bd, total) => setForm((prev) => ({ ...prev, throw_breakdown: bd, throw_volume: total }))}
                />
              )}
            </div>
          )}

          {(showField("chest_accuracy") || showField("pain_during")) && (
            <div className="rounded-xl ink-card p-4 space-y-3">
              {showField("chest_accuracy") && (
                <label className="flex items-center justify-between" data-testid="chest-accuracy-toggle">
                  <div>
                    <div className="font-display font-bold text-sm text-white">75% Chest Accuracy</div>
                    <div className="text-xs text-white/50">Were 75% of your throws to the chest?</div>
                  </div>
                  <Switch checked={form.chest_accuracy_75} onCheckedChange={(v) => setVal("chest_accuracy_75", v)} />
                </label>
              )}
              {showField("pain_during") && (
                <label className="flex items-center justify-between" data-testid="pain-during-toggle">
                  <div>
                    <div className="font-display font-bold text-sm text-white flex items-center gap-2">
                      Pain During Session <AlertTriangle className="h-4 w-4 status-critical" />
                    </div>
                    <div className="text-xs text-white/50">Any shoulder, elbow, or joint pain mid-session?</div>
                  </div>
                  <Switch checked={form.pain_during} onCheckedChange={(v) => setVal("pain_during", v)} />
                </label>
              )}
            </div>
          )}

          {showField("post_fatigue") && <Rating label="Post-Throw Joint Fatigue" sub="1 = none · 5 = exhausted/inflamed" value={form.post_throw_fatigue} onChange={(v) => setVal("post_throw_fatigue", v)} testId="post_throw_fatigue" />}
        </div>

        <div className="mt-6 space-y-4 pb-32">
          <PlayerWorkloadCard />
          <PushSettingsCard />
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 backdrop-blur-xl border-t border-[var(--ink-border)] px-5 py-3 z-30" style={{ backgroundColor: "rgba(10,20,36,0.92)" }}>
        <div className="max-w-2xl mx-auto">
          <Button onClick={submit} disabled={submitting} className="w-full h-13 py-3.5 rounded-full text-base font-bold text-white red-glow"
            style={{ backgroundColor: "var(--brand-secondary)" }} data-testid="submit-log-button">
            <Check className="h-5 w-5 mr-2" />
            {submitting ? "Submitting…" : "Submit today's log"}
          </Button>
        </div>
      </div>

      {/* Quick-log LIVUP FAB — opens the ring-screenshot scanner in one tap */}
      <Link
        to="/portal/livup?scan=1"
        data-testid="quick-log-fab"
        aria-label="Quick log — scan LIVUP screenshot"
        className="fixed right-4 z-40 rounded-full h-14 w-14 flex items-center justify-center shadow-xl backdrop-blur transition hover:scale-105 active:scale-95"
        style={{
          bottom: "calc(env(safe-area-inset-bottom, 0px) + 92px)",
          backgroundColor: "var(--brand-primary)",
          color: "white",
          boxShadow: "0 10px 30px -8px rgba(34,211,238,0.45), 0 0 0 1px rgba(255,255,255,0.08) inset",
        }}
      >
        <Camera className="h-6 w-6" />
        <span className="sr-only">Quick log LIVUP</span>
      </Link>
    </div>
  );
}
